import { FC, useState } from 'react'
import { AuditExportParams, exportAuditEvents } from '../../../services/auditLogService'

const SYNC_CAP = 20_000

interface Props {
  filters: AuditExportParams
  rowCount: number
  onClose: () => void
}

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export const AuditExportModal: FC<Props> = ({ filters, rowCount, onClose }) => {
  const [format] = useState<'csv'>('csv')  // xlsx pending; sync-only backend today
  const [includeChanges, setIncludeChanges] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const overCap = rowCount > SYNC_CAP
  const withinLimit = rowCount <= SYNC_CAP

  const handleExport = async () => {
    if (overCap) return
    setBusy(true)
    setError(null)
    try {
      const blob = await exportAuditEvents({ ...filters, includeChanges })
      const stamp = new Date().toISOString().replace(/[:.]/g, '-')
      triggerDownload(blob, `audit-log-${stamp}.${format}`)
      onClose()
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Export failed.'
      setError(String(detail))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="nb-audit-export" onClick={onClose}>
      <div className="nb-audit-export__panel" onClick={(e) => e.stopPropagation()}>
        <h3 className="nb-audit-export__title">Export audit events</h3>

        <div className="nb-audit-export__field">
          <span className="nb-audit-export__field-label">Format</span>
          <div className="nb-audit-export__radio-row">
            <label className="is-active">
              <input type="radio" name="format" defaultChecked /> CSV
            </label>
            <label style={{ opacity: 0.4, cursor: 'not-allowed' }} title="Backend export is CSV-only for now">
              <input type="radio" name="format" disabled /> Excel (.xlsx)
            </label>
          </div>
        </div>

        <div className="nb-audit-export__field">
          <span className="nb-audit-export__field-label">Scope</span>
          <div className="nb-audit-export__summary">
            <span>Active filters are preserved on the export.</span>
            <span>Estimated rows: <b>{rowCount.toLocaleString()}</b></span>
          </div>
        </div>

        <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontFamily: "'Satoshi', sans-serif", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={includeChanges}
            onChange={(e) => setIncludeChanges(e.target.checked)}
          />
          Include full changes column (larger file)
        </label>

        {withinLimit && (
          <div className="nb-audit-export__banner nb-audit-export__banner--amber">
            Under the sync limit — the file will download immediately.
          </div>
        )}
        {overCap && (
          <div className="nb-audit-export__banner nb-audit-export__banner--red">
            Above the {SYNC_CAP.toLocaleString()} row cap. Narrow the filter to continue —
            async exports arrive with the background worker follow-up.
          </div>
        )}
        {error && (
          <div className="nb-audit-export__banner nb-audit-export__banner--red">{error}</div>
        )}

        <div className="nb-audit-export__actions">
          <button className="nb-audit__btn nb-audit__btn--outline" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="nb-audit__btn nb-audit__btn--primary"
            onClick={handleExport}
            disabled={busy || overCap}
          >
            {busy ? 'Exporting…' : `Export ${rowCount.toLocaleString()} events`}
          </button>
        </div>
      </div>
    </div>
  )
}
